import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErrorState } from "@/components/common/EmptyState";
import { FEEDBACK_TYPES, STATUS_LABEL } from "@/components/feedback/FeedbackDialog";
import { TypeBadge } from "@/pages/Feedback";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, API, errorMessage } from "@/lib/api";
import { agentName } from "@/lib/catalogue";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const Pick = ({ id, value, onChange, options, label }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full rounded-xl bg-card sm:w-[170px]" aria-label={label} data-testid={`fb-filter-${id}`}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-80 rounded-xl">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const Item = ({ f, onSaved }) => {
  const [reply, setReply] = useState(f.reply || "");
  const save = async (changes, msg) => {
    try { const { data } = await api.patch(`/admin/feedback/${f.id}`, changes); onSaved(data); toast.success(msg); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <li className={cn("card-elevated p-5", f.type === "guardrail" && "border-l-4 border-l-destructive")} data-testid={`inbox-item-${f.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <TypeBadge type={f.type} />
        <span className="font-medium">{f.about_name}</span>
        <span className="text-muted-foreground">· {f.author_name} · rating {f.rating} of 5 · {formatDateTime(f.created_at)}</span>
        <div className="ml-auto">
          <Select value={f.status} onValueChange={(v) => save({ status: v }, `Marked ${STATUS_LABEL[v].toLowerCase()}`)}>
            <SelectTrigger className="h-9 w-[150px] rounded-xl" aria-label="Status" data-testid={`inbox-status-${f.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-xl">{Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-sm">{f.text}</p>
      <div className="mt-2 flex flex-wrap gap-4 text-xs">
        {f.run_link && <a href={f.run_link} target="_blank" rel="noreferrer noopener" className="underline underline-offset-4" data-testid="inbox-run-link">Open the run</a>}
        {f.screenshot_file_id && <a href={`${API}/files/${f.screenshot_file_id}`} target="_blank" rel="noreferrer" className="underline underline-offset-4" data-testid="inbox-screenshot">View screenshot</a>}
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <Textarea value={reply} maxLength={2000} rows={2} onChange={(e) => setReply(e.target.value)} placeholder="Reply to the author" aria-label="Reply" className="rounded-xl" data-testid={`inbox-reply-${f.id}`} />
        <Button variant="secondary" size="sm" disabled={reply === (f.reply || "")} onClick={() => save({ reply }, "Reply saved")} data-testid={`inbox-reply-save-${f.id}`}>Save reply</Button>
      </div>
    </li>
  );
};

const Inbox = () => {
  const { teams, agents, workflows } = useCatalogue();
  const [f, setF] = useState({ about_type: "all", type: "all", status: "all", about_id: "all", team: "all", days: "all" });
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all"));
  const key = JSON.stringify(params);
  const load = useCallback(() => api.get("/admin/feedback", { params: JSON.parse(key) }).then(({ data }) => { setItems(data); setError(""); }).catch((e) => setError(errorMessage(e))), [key]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
        <Pick id="about" label="About" value={f.about_type} onChange={set("about_type")} options={[["all", "About anything"], ["agent", "Agents"], ["workflow", "Workflows"], ["team", "Teams"], ["portal", "The portal"]]} />
        <Pick id="type" label="Type" value={f.type} onChange={set("type")} options={[["all", "All types"], ...FEEDBACK_TYPES]} />
        <Pick id="status" label="Status" value={f.status} onChange={set("status")} options={[["all", "All statuses"], ...Object.entries(STATUS_LABEL)]} />
        <Pick id="agent" label="Agent or workflow" value={f.about_id} onChange={set("about_id")} options={[["all", "All agents"], ...workflows.map((w) => [w.id, w.name]), ...agents.map((a) => [a.emp_no, agentName(a)])]} />
        <Pick id="team" label="Team" value={f.team} onChange={set("team")} options={[["all", "All teams"], ...teams.map((t) => [t.id, t.section || t.name])]} />
        <Pick id="days" label="Date" value={f.days} onChange={set("days")} options={[["all", "Any date"], ["1", "Last 24 hours"], ["7", "Last 7 days"], ["30", "Last 30 days"]]} />
      </div>
      {error ? <ErrorState message={error} onRetry={load} /> : !items ? <Skeleton className="h-40 rounded-xl" /> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="inbox-empty">No feedback matches. New feedback lands here as it arrives.</p>
      ) : (
        <ul className="space-y-3" data-testid="inbox-list">{items.map((x) => <Item key={x.id} f={x} onSaved={(d) => setItems((l) => l.map((y) => (y.id === d.id ? d : y)))} />)}</ul>
      )}
    </div>
  );
};

const Summary = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/feedback/summary").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <Skeleton className="h-40 rounded-xl" />;
  if (!rows.length) return <p className="text-sm text-muted-foreground" data-testid="summary-empty">No feedback about agents or workflows yet.</p>;
  return (
    <div className="card-elevated overflow-x-auto" data-testid="feedback-summary">
      <Table>
        <TableHeader><TableRow><TableHead>Agent or workflow</TableHead>{FEEDBACK_TYPES.map(([v, l]) => <TableHead key={v}>{l}</TableHead>)}<TableHead>Average rating</TableHead><TableHead>Last 30 days</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => {
            const max = Math.max(1, ...r.daily);
            return (
              <TableRow key={r.about_id} data-testid={`summary-row-${r.about_id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                {FEEDBACK_TYPES.map(([v]) => <TableCell key={v} className={cn("text-sm", v === "guardrail" && r.by_type[v] > 0 && "font-semibold text-destructive")}>{r.by_type[v]}</TableCell>)}
                <TableCell className="text-sm">{r.average_rating}</TableCell>
                <TableCell>
                  <div className="flex items-end gap-px" aria-hidden="true">{r.daily.map((d, i) => <span key={i} className="w-1 rounded-sm bg-foreground/60" style={{ height: `${4 + (d / max) * 20}px`, opacity: d ? 1 : 0.2 }} />)}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{r.last_30} items, {r.prev_30} the 30 days before</p>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default function FeedbackAdmin() {
  return (
    <Tabs defaultValue="inbox" data-testid="admin-feedback">
      <TabsList className="rounded-xl"><TabsTrigger value="inbox" data-testid="fb-tab-inbox">Inbox</TabsTrigger><TabsTrigger value="summary" data-testid="fb-tab-summary">Per-agent summary</TabsTrigger></TabsList>
      <TabsContent value="inbox" className="mt-4"><Inbox /></TabsContent>
      <TabsContent value="summary" className="mt-4"><Summary /></TabsContent>
    </Tabs>
  );
}
